import { useEffect, useRef, useState } from "react";

// Top loading bar (YouTube/GitHub style). Tracks active requests via a module-level
// counter so multiple concurrent calls don't make the bar flicker or finish early.
// Use startLoading() / endLoading() from the Apollo link and axios interceptors below.

let activeCount = 0;
const listeners = new Set();

export const startLoading = () => {
  activeCount += 1;
  listeners.forEach((fn) => fn(activeCount));
};

export const endLoading = () => {
  activeCount = Math.max(0, activeCount - 1);
  listeners.forEach((fn) => fn(activeCount));
};

const LoadingBar = () => {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const handler = (count) => {
      if (count > 0) {
        // Active requests in flight. Start or continue advancing the bar.
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
        setProgress((p) => (p < 10 ? 10 : p));
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            // Asymptotically approach 90% so the bar never "completes" prematurely.
            setProgress((p) => (p < 90 ? p + Math.max(0.5, (90 - p) / 50) : p));
          }, 200);
        }
      } else {
        // No active requests — finish the bar and hide.
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setProgress(100);
        hideTimerRef.current = setTimeout(() => {
          setProgress(0);
          hideTimerRef.current = null;
        }, 300);
      }
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (progress === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: `${progress}%`,
        height: 4,
        // Bright amber stands out against both the blue app header and white content
        backgroundColor: "#f59e0b",
        boxShadow: "0 0 10px rgba(245,158,11,0.9), 0 0 4px rgba(245,158,11,1)",
        zIndex: 2147483647,
        transition: "width 200ms ease-out, opacity 300ms ease-out",
        opacity: progress >= 100 ? 0 : 1,
        pointerEvents: "none",
      }}
    />
  );
};

export default LoadingBar;
